import React, { useMemo } from 'react';
import './DataExercise.scss';
import { Stack, Typography } from '@mui/material';
import { round } from 'lodash';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import {
  dataTrendPeriodAtom,
  getWeekDatesOf,
  selectedDateAtom,
  useAuthContext,
  useExerciseWeeklyTrend,
  useMetricsContext,
} from '../../../../data-access';
import {
  getExerciseDetailsForDate,
  getVisibleExerciseActivitiesForDate,
} from './DataExercise.helpers';

const DataExercise: React.FC = () => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const { user } = useAuthContext();
  const { exerciseDetails } = useMetricsContext();
  const isWeekly = dataTrendPeriod === 'weekly';

  const weekEdge = useMemo(() => {
    const weekDates = getWeekDatesOf(selectedDate);
    return {
      startDate: weekDates[0].format('YYYY-MM-DD'),
      endDate: weekDates[weekDates.length - 1].format('YYYY-MM-DD'),
    };
  }, [selectedDate]);

  const { exerciseTrend } = useExerciseWeeklyTrend({
    dataTrendId: user.dataTrendId!,
    metricId: user.metricId!,
    startDate: weekEdge.startDate,
    endDate: weekEdge.endDate,
    enabled: isWeekly,
  });

  const dailyActivities = useMemo(
    () => getVisibleExerciseActivitiesForDate(exerciseDetails, selectedDate),
    [exerciseDetails, selectedDate]
  );

  const dailyExerciseDetails = useMemo(
    () => getExerciseDetailsForDate(exerciseDetails, selectedDate),
    [exerciseDetails, selectedDate]
  );

  const dailyTotals = useMemo(() => {
    const activityTotals = dailyActivities.reduce(
      (acc, activity) => ({
        steps: acc.steps + (activity.steps ?? 0),
        calories: acc.calories + (activity.calories ?? 0),
        distanceMeters: acc.distanceMeters + (activity.distanceMeters ?? 0),
      }),
      { steps: 0, calories: 0, distanceMeters: 0 }
    );

    return {
      steps: dailyExerciseDetails?.summary?.steps ?? activityTotals.steps,
      calories: activityTotals.calories,
      distanceMeters: dailyExerciseDetails?.summary?.distanceMeters ?? activityTotals.distanceMeters,
    };
  }, [dailyActivities, dailyExerciseDetails]);

  const totals = isWeekly
    ? {
        steps: exerciseTrend?.totalSteps ?? 0,
        calories: exerciseTrend?.totalCalories ?? 0,
        distanceMeters: exerciseTrend?.totalDistanceMeters ?? 0,
      }
    : dailyTotals;

  const hasData =
    (isWeekly
      ? (exerciseTrend?.activeDays ?? 0) > 0
      : dailyActivities.length > 0 || totals.steps > 0 || totals.distanceMeters > 0) &&
    (totals.steps > 0 || totals.calories > 0 || totals.distanceMeters > 0);

  if (!hasData) return null;

  const supportedStats: Array<{ id: string; label: string; value: string }> = [];
  if (totals.steps > 0) {
    supportedStats.push({
      id: 'steps',
      label: t('data.exercise.steps', { defaultValue: 'Steps' }),
      value: totals.steps.toLocaleString(),
    });
  }
  if (totals.distanceMeters > 0) {
    supportedStats.push({
      id: 'distance',
      label: t('data.exercise.distance', { defaultValue: 'Distance' }),
      value: `${round(totals.distanceMeters / 1000, 2)} km`,
    });
  }

  return (
    <div className="data-exercise">
      <Typography variant="body1" className="data-exercise__title">
        {t('data.exercise.title', { defaultValue: 'Exercise' })}
      </Typography>
      <div className="data-exercise__calories">
        <Typography variant="h2" className="data-exercise__calories-value">
          {totals.calories > 0 ? `${round(totals.calories)} kcal` : '--'}
        </Typography>
        <Typography variant="body2" className="data-exercise__calories-label">
          {isWeekly
            ? t('data.exercise.caloriesBurnedThisWeek', {
                defaultValue: 'Calories burned this week',
              })
            : t('data.exercise.caloriesBurned', { defaultValue: 'Calories burned' })}
        </Typography>
      </div>
      {supportedStats.length > 0 && (
        <div className="data-exercise__box">
          <Stack direction="row" className="data-exercise__stats">
            {supportedStats.map(({ id, label, value }) => (
              <div key={id} className="data-exercise__stat-item">
                <Typography variant="body1" className="data-exercise__stat-value">
                  {value}
                </Typography>
                <Typography variant="body2" className="data-exercise__stat-label">
                  {label}
                </Typography>
              </div>
            ))}
          </Stack>
        </div>
      )}
    </div>
  );
};

export default DataExercise;
